import { Component, computed, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ControlConfig } from '../../../models/api/reports/report';
import { RangeFieldComponent } from '../../core/fields/range-field.component';
import { SelectFieldComponent } from '../../core/fields/select-field.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { BaseCriteriaComponent } from './base-criteria.component';

interface ReserveCriteriaFormControls {
  reserveChangeAmountFrom?: FormControl<number | null>;
  reserveChangeAmountThrough?: FormControl<number | null>;
  financialType?: FormControl<string | null>;
}

@Component({
  selector: 'app-reserve-criteria',
  imports: [PageHeaderComponent, RangeFieldComponent, SelectFieldComponent],
  template: `
    <div class="tw-flex tw-flex-col tw-w-full tw-gap-2">
      <app-page-header label="Reserve Criteria" headerSize="h3" />

      <!-- Reserve Change Amount Range (From/Through) -->
      @if (showReserveChangeAmount()) {
        <app-range-field
          label="Reserve Change Amount"
          valueClasses="tw-max-w-[400px]"
          [control]="fg.controls.reserveChangeAmountFrom"
          [toControl]="fg.controls.reserveChangeAmountThrough"
          fromLabel="From"
          toLabel="Through"
          [compact]="true"
          type="Currency"
          [requireBoth]="true"
        />
      }

      <!-- Financial Type Dropdown -->
      @if (showFinancialType()) {
        <app-select-field
          [label]="getLabel('FINANCIAL_TYPE') || 'Financial Type'"
          valueClasses="tw-max-w-[200px]"
          [control]="fg.controls.financialType"
          [options]="financialTypeOptions()"
          optionLabel="name"
          optionValue="code"
          placeholder="Select Financial Type"
          [showClear]="false"
          [filter]="false"
          [virtualScroll]="false"
          [disabled]="!areAmountsFilled()"
        />
      }
    </div>
  `,
})
export class ReserveCriteriaComponent extends BaseCriteriaComponent {
  showReserveChangeAmount = signal(false);
  showFinancialType = signal(false);

  financialTypeOptions = computed(() =>
    this.controlConfig.getControlOptions('FINANCIAL_TYPE'),
  );

  fg = new FormGroup<ReserveCriteriaFormControls>({});

  protected readonly regionName = 'Reserve Criteria';

  protected CONTROL_CONFIGS: ControlConfig[] = [
    {
      erfNames: 'RESERVE_CHANGE_AMOUNT_FROM',
      formControlName: 'reserveChangeAmountFrom',
      visibility: this.showReserveChangeAmount,
      type: 'number',
    },
    {
      erfNames: 'RESERVE_CHANGE_AMOUNT_THROUGH',
      formControlName: 'reserveChangeAmountThrough',
      visibility: this.showReserveChangeAmount,
      type: 'number',
    },
    {
      erfNames: 'FINANCIAL_TYPE',
      formControlName: 'financialType',
      visibility: this.showFinancialType,
      defaultToFirstOption: false,
    },
  ];

  override ngOnInit(): void {
    super.ngOnInit();
    this.updateFinancialTypeState();
  }

  protected override setupFormListeners(): void {
    if (this.fg.controls.reserveChangeAmountFrom) {
      this.dispose.subs =
        this.fg.controls.reserveChangeAmountFrom.valueChanges.subscribe(() => {
          this.updateFinancialTypeState();
        });
    }

    if (this.fg.controls.reserveChangeAmountThrough) {
      this.dispose.subs =
        this.fg.controls.reserveChangeAmountThrough.valueChanges.subscribe(
          () => {
            this.updateFinancialTypeState();
          },
        );
    }
  }

  areAmountsFilled(): boolean {
    const fromValue = this.fg.controls.reserveChangeAmountFrom?.value;
    const throughValue = this.fg.controls.reserveChangeAmountThrough?.value;
    return fromValue != null && throughValue != null;
  }

  private updateFinancialTypeState(): void {
    const financialTypeControl = this.fg.controls.financialType;
    if (!financialTypeControl) return;

    if (this.areAmountsFilled()) {
      financialTypeControl.enable({ emitEvent: false });
    } else {
      financialTypeControl.disable({ emitEvent: false });
    }
  }

  protected getLabel(erfName: string): string | undefined {
    return this.controlConfig.getControlLabel(erfName);
  }
}
